import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatIconModule } from '@angular/material/icon';
import { MatChipsModule } from '@angular/material/chips';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { PlayerAuctionService } from '../player-auction.service';
import { ProviderOrderStatusResponse } from '../player-auction.type';
import { parseBackupCodes, pastedBackupCodes } from '../../shared/credentials';

const STATUS_LABELS: Record<string, string> = {
  processing: 'Transferring',
  resting: 'Resting',
  stopped: 'Stopped',
  failed: 'Failed',
  completed: 'Done',
  unknown: 'Pending',
};

@Component({
  selector: 'app-outsourcing-status',
  imports: [
    CommonModule,
    FormsModule,
    MatProgressBarModule,
    MatProgressSpinnerModule,
    MatIconModule,
    MatChipsModule,
    MatButtonModule,
    MatInputModule,
    MatFormFieldModule,
  ],
  templateUrl: './outsourcing-status.html',
  styleUrl: '../contract-status/contract-status.scss',
})
// Public, customer-facing tracking page for an order held by an outsourcing
// provider (e.g. Futtransfer), keyed by the provider's order ID. Talks to the
// standalone provider-order endpoints, so it works for handed-off
// contracts and for orders placed directly with the provider.
export class OutsourcingStatusComponent implements OnInit {
  private _route = inject(ActivatedRoute);
  private _service = inject(PlayerAuctionService);
  private _destroyRef = inject(DestroyRef);

  orderId = '';
  provider: string | null = null;
  order: ProviderOrderStatusResponse | null = null;
  loading = true;
  fetchError = false;

  startScreenshotUrl: string | null = null;
  endScreenshotUrl: string | null = null;
  startScreenshotFetched = false;
  endScreenshotFetched = false;

  actionLoading = false;
  actionError: string | null = null;

  showCredsForm = false;
  credsPassword = '';
  credsBackupCodes = '';
  correctCredsLoading = false;
  correctCredsError: string | null = null;

  private _pollTimer: ReturnType<typeof setTimeout> | null = null;
  private _destroyed = false;

  ngOnInit(): void {
    this.orderId = this._route.snapshot.paramMap.get('orderId')!;
    this.provider = this._route.snapshot.queryParamMap.get('provider');
    this._destroyRef.onDestroy(() => {
      this._destroyed = true;
      this._clearTimer();
      if (this.startScreenshotUrl) URL.revokeObjectURL(this.startScreenshotUrl);
      if (this.endScreenshotUrl) URL.revokeObjectURL(this.endScreenshotUrl);
    });
    this._poll();
  }

  private _poll(): void {
    this._service.getProviderOrderStatus(this.orderId, this.provider).subscribe({
      next: (status) => {
        this.order = status;
        this.loading = false;
        this.fetchError = false;
        this._fetchScreenshots(status);
        if (!this._destroyed && !this.isTerminal) {
          this._pollTimer = setTimeout(() => this._poll(), 5_000);
        }
      },
      error: () => {
        this.loading = false;
        this.fetchError = true;
        if (!this._destroyed) this._pollTimer = setTimeout(() => this._poll(), 10_000);
      },
    });
  }

  private _fetchScreenshots(status: ProviderOrderStatusResponse): void {
    const load = (type: 'start' | 'end') => {
      this._service.getProviderOrderScreenshot(this.orderId, type, this.provider).subscribe({
        next: (blob) => {
          const url = URL.createObjectURL(blob);
          if (type === 'start') {
            if (this.startScreenshotUrl) URL.revokeObjectURL(this.startScreenshotUrl);
            this.startScreenshotUrl = url;
          } else {
            if (this.endScreenshotUrl) URL.revokeObjectURL(this.endScreenshotUrl);
            this.endScreenshotUrl = url;
          }
        },
        // The provider may not have a screenshot yet; allow another attempt on a later poll.
        error: () => {
          if (type === 'start') this.startScreenshotFetched = false;
          else this.endScreenshotFetched = false;
        },
      });
    };

    if (!this.startScreenshotFetched && status.status !== 'unknown') {
      this.startScreenshotFetched = true;
      load('start');
    }
    if (!this.endScreenshotFetched && status.status === 'completed') {
      this.endScreenshotFetched = true;
      load('end');
    }
  }

  doResume(): void {
    if (this.actionLoading) return;
    this.actionLoading = true;
    this.actionError = null;
    this._service.updateProviderOrderState(this.orderId, 'resume', this.provider).subscribe({
      next: () => {
        this.actionLoading = false;
        this._refreshNow();
      },
      error: (err: any) => {
        this.actionLoading = false;
        this.actionError = err?.error?.error ?? 'Action failed. Please try again.';
      },
    });
  }

  onBackupCodesPaste(event: ClipboardEvent): void {
    const codes = pastedBackupCodes(event);
    this.credsBackupCodes = [...parseBackupCodes(this.credsBackupCodes), ...codes].join(', ');
  }

  doCorrectCreds(): void {
    if (this.correctCredsLoading) return;
    const request: { password?: string; backup_codes?: string[] } = {};
    if (this.credsPassword.trim()) request.password = this.credsPassword.trim();
    const codes = parseBackupCodes(this.credsBackupCodes);
    if (codes.length) request.backup_codes = codes;
    if (!request.password && !request.backup_codes) {
      this.correctCredsError = 'Enter a new password or at least one backup code.';
      return;
    }
    this.correctCredsLoading = true;
    this.correctCredsError = null;
    this._service.correctProviderOrderCredentials(this.orderId, request, this.provider).subscribe({
      next: () => {
        this.correctCredsLoading = false;
        this.showCredsForm = false;
        this.credsPassword = '';
        this.credsBackupCodes = '';
        this._refreshNow();
      },
      error: (err: any) => {
        this.correctCredsLoading = false;
        this.correctCredsError = err?.error?.error ?? 'Failed to update credentials.';
      },
    });
  }

  private _refreshNow(): void {
    this._clearTimer();
    this._poll();
  }

  private _clearTimer(): void {
    if (this._pollTimer !== null) {
      clearTimeout(this._pollTimer);
      this._pollTimer = null;
    }
  }

  get statusLabel(): string {
    return STATUS_LABELS[this.order?.status ?? 'unknown'] ?? '';
  }

  get isTerminal(): boolean {
    return this.order?.status === 'completed';
  }

  get canResume(): boolean {
    return this.order?.status === 'stopped' || this.order?.status === 'failed';
  }

  get showLoginWarning(): boolean {
    return !!this.order && !this.isTerminal && this.order.status !== 'failed';
  }

  get progressPercentage(): number {
    const target = this.order?.target_coins || 0;
    if (!target) return 0;
    return Math.min(100, Math.floor(((this.order?.transferred_coins || 0) / target) * 100));
  }
}
